import React, { useState } from 'react';
import {
  FileText, Upload, Video, Link2, Plus,
} from 'lucide-react';
import { fetchJSON, API_BASE_URL } from '../../utils/api';
import { fmtBytes } from '../workflow/constants';
import { formatDateTime } from '../../utils/dateFormat';
import { FilePicker, inputCls } from '../shared/FormKit';
import FileList, { RemoveFileButton } from '../shared/FileList';
import ConfirmDeleteModal from '../shared/ConfirmDeleteModal';
import { hintOf } from '../../utils/uploadKinds';

/**
 * Course materials / recordings on a training session (docs/modules/ld.md
 * §3.6) — the list, and (when `canManage`) upload, add a link, remove.
 *
 * One component, two kinds:
 *   kind="material"   slides and handouts
 *   kind="recording"  the course video, or a link to wherever a bigger file
 *                     lives
 * What each one accepts and how big it may be is its row in
 * utils/uploadKinds (trainingMaterial / recording).
 * Either kind may hold an external link (a deck on a shared drive, a video on
 * a streaming host): the row then opens the link instead of a stored file.
 *
 * Used by the session page (participants read, trainers manage), the
 * Training Records View modal (L&D manages) and the dashboard's expanded
 * session row (read-only). The server decides who may change what;
 * `canManage` only shows the controls.
 *
 * Downloads are plain anchors on the authenticated GET — PDFs, images and
 * videos open in the browser, office files download (utils/fileResponse.js);
 * a link row's anchor is the link itself.
 */

const KIND_META = {
  material: {
    kind: 'trainingMaterial',
    uploadPath: (id) => `/api/ld/sessions/${id}/files`,
    button: 'Upload Materials',
    hint: hintOf('trainingMaterial', { multiple: true }),
    emptyManage: 'No materials yet — upload slides or handouts for the participants, or add a link.',
    emptyRead: 'No materials have been uploaded for this session.',
    icon: FileText,
    removeTitle: 'Remove material',
    linkHint: 'e.g. a deck on SharePoint',
  },
  recording: {
    kind: 'recording',
    uploadPath: (id) => `/api/ld/sessions/${id}/files/recordings`,
    button: 'Upload Video',
    hint: `${hintOf('recording', { multiple: true })} For a larger video, add a link to where it is hosted.`,
    emptyManage: 'No recording yet — upload the course video after the session, or add a link to it.',
    emptyRead: 'No recording has been added for this session.',
    icon: Video,
    removeTitle: 'Remove recording',
    linkHint: 'e.g. a video on Stream, Teams or a shared drive',
  },
};


const SessionMaterials = ({ sessionId, kind = 'material', files = [], canManage = false, onChanged, compact = false }) => {
  const meta = KIND_META[kind] || KIND_META.material;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(null); // the row awaiting confirmation
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');

  const upload = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      picked.forEach((f) => fd.append('files', f));
      const data = await fetchJSON(meta.uploadPath(sessionId), { method: 'POST', body: fd });
      if (onChanged) onChanged(data.data || []);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const addLink = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/ld/sessions/${sessionId}/files/links`, {
        method: 'POST', body: JSON.stringify({ kind, url: linkUrl.trim(), title: linkTitle.trim() }),
      });
      setLinkOpen(false);
      setLinkUrl('');
      setLinkTitle('');
      if (onChanged) onChanged(data.data || []);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const remove = async (file) => {
    const data = await fetchJSON(`/api/ld/sessions/${sessionId}/files/${file.id}`, { method: 'DELETE' });
    setRemoving(null);
    if (onChanged) onChanged(data.data || []);
  };

  const pad = compact ? 'px-3 py-2' : 'px-5 py-3';
  const Icon = meta.icon;

  return (
    <div>
      {files.length === 0 ? (
        <p className={`text-sm text-slate-500 ${compact ? 'px-3 py-3' : 'px-5 py-6'} text-center`}>
          {canManage ? meta.emptyManage : meta.emptyRead}
        </p>
      ) : (
        <FileList
          files={files}
          row={(f) => ({
            // A link row points at wherever the file actually lives; the
            // stored ones point at this session's download.
            name: f.originalName,
            href: f.url || `${API_BASE_URL}/api/ld/sessions/${sessionId}/files/${f.id}`,
            external: !!f.url,
            icon: f.url ? Link2 : Icon,
            stacked: true,
            pad,
            nameClass: 'break-all',
            longMeta: true,
            meta: [
              f.url ? f.url : fmtBytes(f.sizeBytes),
              f.uploadedBy || null,
              f.createdAt ? formatDateTime(f.createdAt) : null,
            ],
            actions: canManage
              ? <RemoveFileButton disabled={busy} onClick={() => setRemoving(f)} />
              : null,
          })}
        />
      )}

      {canManage && (
        <div className={`border-t border-slate-100 ${pad} space-y-2`}>
          <div className="flex flex-wrap items-center gap-3">
            <FilePicker
              multiple
              kind={meta.kind}
              disabled={busy}
              onChange={upload}
              showSelection={false}
              buttonLabel={busy ? 'Working…' : meta.button}
            />
            <button
              type="button"
              onClick={() => setLinkOpen((v) => !v)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <Link2 className="w-4 h-4" /> Add Link
            </button>
            <span className="text-xs text-slate-500">{meta.hint}</span>
            {busy && <Upload className="w-4 h-4 text-slate-400 animate-pulse" />}
          </div>
          {linkOpen && (
            <div className="flex flex-wrap items-end gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
              <label className="flex-1 min-w-[220px] text-xs text-slate-600">
                Link
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className={`${inputCls} mt-1`}
                  placeholder="https://…"
                  maxLength={2000}
                />
              </label>
              <label className="flex-1 min-w-[180px] text-xs text-slate-600">
                Title
                <input
                  type="text"
                  value={linkTitle}
                  onChange={(e) => setLinkTitle(e.target.value)}
                  className={`${inputCls} mt-1`}
                  placeholder={meta.linkHint}
                  maxLength={255}
                />
              </label>
              <button
                type="button"
                onClick={addLink}
                disabled={busy || !linkUrl.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
              <button type="button" onClick={() => setLinkOpen(false)} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className={`${pad} text-sm text-red-700 bg-red-50 border-t border-red-100`}>
          {error}
        </div>
      )}

      {removing && (
        <ConfirmDeleteModal
          title={meta.removeTitle}
          message={`Remove “${removing.originalName}” from this session? Participants will no longer see it. This cannot be undone.`}
          confirmLabel="Remove"
          onClose={() => setRemoving(null)}
          onConfirm={() => remove(removing)}
        />
      )}
    </div>
  );
};

export default SessionMaterials;
